import React, { useMemo } from 'react';
import moment from 'moment-timezone';
import { Popover } from 'antd';
import { roundToNearest } from '../../utils/DataFormatterUtils';

const DateTimeHover = ({
	timestamp,
	showTime = true,
	showYear = true,
	showDate = false,
	parseZone = true,
	keepLocalTime = false,
	dateFormat = undefined,
	timeZone = 'UTC',
}) => {
	const now = moment();
	const dateTime = useMemo(() => {
		return parseZone ? moment.parseZone(timestamp) : moment(timestamp);
	}, [parseZone, timestamp]);

	const dateTimeFormattedWithTimeZone = useMemo(
		() => (dateTime.tz(timeZone) ? dateTime.tz(timeZone) : dateTime),
		[dateTime, timeZone]
	);

	const fullDateTime = useMemo(() => {
		if (!dateTime || !dateTime.isValid() || !dateTimeFormattedWithTimeZone) {
			return <span>-</span>;
		}
		return dateFormat
			? dateTimeFormattedWithTimeZone.format(dateFormat)
			: dateTimeFormattedWithTimeZone.format(
					showTime
						? `MMM D${showYear ? ', YYYY' : ''} [at] h:mm a z`
						: `MMM D${showYear ? ', YYYY' : ''}`
			  );
	}, [dateFormat, showTime, showYear, dateTime, timeZone]);

	const secondsDiff = useMemo(() => now.diff(dateTime, 'seconds'), [now, dateTime]);
	const minutesDiff = useMemo(
		() => now.startOf('minute').diff(dateTime.clone().startOf('minute'), 'minutes'),
		[now, dateTime]
	);
	const hoursDiff = useMemo(
		() => roundToNearest(now.diff(dateTime, 'hours', true), 1),
		[now, dateTime]
	);
	const daysDiff = useMemo(
		() => now.startOf('day').diff(dateTime.clone().local(keepLocalTime).startOf('day'), 'days'),
		[now, dateTime]
	);

	const hoverContent = (
		<span>
			{dateTimeFormattedWithTimeZone.format('MMMM D, YYYY [at] h:mm a z').replace('UTC', '')}
		</span>
	);
	let showHover = true;

	let displayText;
	const formatText = (diff, unit) =>
		`${Math.abs(diff) === 1 ? 'a' : Math.abs(diff)} ${unit}${Math.abs(diff) > 1 ? 's' : ''}`;

	if (daysDiff < -7 || showDate) {
		displayText = fullDateTime;
		showHover = false;
	} else if (daysDiff < 0) {
		displayText = `in ${formatText(-daysDiff, 'day')} ${
			showTime ? dateTimeFormattedWithTimeZone.format(`[at] h:mm a z`) : ''
		}`;
	} else if (hoursDiff < 0) {
		displayText = `in ${formatText(-hoursDiff, 'hour')}`;
	} else if (minutesDiff < 0) {
		displayText = `in ${formatText(-minutesDiff, 'minute')}`;
	} else if (secondsDiff < 0) {
		displayText = `in ${formatText(-secondsDiff, 'second')}`;
	} else if (secondsDiff < 5) {
		displayText = `just now`;
	} else if (secondsDiff < 60) {
		displayText = `${formatText(secondsDiff, 'second')} ago`;
	} else if (minutesDiff < 60) {
		displayText = `${formatText(minutesDiff, 'minute')} ago`;
	} else if (hoursDiff < 24) {
		displayText = `${formatText(-hoursDiff, 'hour')} ago`;
	} else if (daysDiff < 7) {
		displayText = `${formatText(daysDiff, 'day')} ago ${
			showTime ? dateTime.format(`[at] h:mm a z`) : ''
		}`;
	} else {
		displayText = fullDateTime;
		showHover = false;
	}

	displayText = displayText.replace('UTC', '');
	return showHover ? (
		<Popover content={hoverContent}>
			<span>{displayText}</span>
		</Popover>
	) : (
		<span>{displayText}</span>
	);
};

export default DateTimeHover;
